import {LoadingDots} from '@commercial-helios/ui/src/components/loading-dots'
import {Script} from 'gatsby'
import React, {useEffect, useState} from 'react'
import styled from 'styled-components'

import PlayButton from '@/components/play-button'
import {Scripts} from '@/utils/external-scripts'
import Image from '@helios/shared/components/image'
import type {ImageSource} from '@helios/shared/types/image'

const MAX_WIDTH = 1240
const MAX_HEIGHT = 750

export enum AspectRatio {
    Ratio169 = (9 / 16) * 100,
    RatioCinemascope = 45,
}

interface Props {
    thumbnail?: ImageSource
    aspectRatio?: AspectRatio
    autoPlay?: boolean
    maxHeight?: number
    videoId: string
    onRequestVideo?: () => void
}

const InlineWistiaVideo = ({
    videoId,
    thumbnail,
    autoPlay,
    aspectRatio = AspectRatio.Ratio169,
    maxHeight,
    onRequestVideo,
}: Props) => {
    const [requestedVideoId, setRequestedVideoId] = useState(autoPlay ? videoId : '')
    const [video, setVideo] = useState<Wistia.Video | null>(null)
    const [playState, setPlayState] = useState<Wistia.PlayState>('beforeplay')
    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        window._wq = window._wq || []

        const events: Wistia.PlayerEvent[] = ['play', 'pause', 'end']

        window._wq.push({
            id: videoId,
            onEmbedded: (video) => {
                setVideo(video)

                events.forEach((event) => {
                    if (videoId === requestedVideoId) {
                        setIsLoading(false)
                        video?.play()
                    }
                    video.bind(event, () => setPlayState(video.state()))
                })
            },
        })

        return () => {
            events.forEach((event) => {
                video?.unbind(event, () => setPlayState(video.state()))
            })
        }
    }, [video, videoId, requestedVideoId])

    useEffect(() => {
        if (requestedVideoId) {
            setIsLoading(true)
            onRequestVideo && onRequestVideo()
        }
    }, [requestedVideoId, onRequestVideo])

    const showThumbnail =
        videoId !== requestedVideoId || ['beforeplay', 'ended'].includes(playState)

    return (
        <>
            {requestedVideoId && (
                <Script
                    src={Scripts.WISTIA}
                    onError={() => console.error(`Wistia script failed to load.`)}
                />
            )}
            <Wrapper
                $aspectRatio={aspectRatio}
                $maxHeight={Math.min(maxHeight ?? MAX_HEIGHT, MAX_HEIGHT)}
            >
                {showThumbnail && thumbnail && <VideoCover image={thumbnail} width={MAX_WIDTH} />}

                {videoId !== requestedVideoId && (
                    <StyledPlayButton onClick={() => setRequestedVideoId(videoId)} />
                )}

                {isLoading && <LoadingDots />}

                {playState === 'ended' && (
                    <StyledPlayButton onClick={() => !!video && video.play()} />
                )}

                <WistiaWrapper
                    $videoEnded={playState === 'ended'}
                    $idleVideo={videoId !== requestedVideoId}
                    className={`
                        wistia_embed wistia_async_${videoId}
                        playButton=false
                        videoFoam=true
                        controlsVisibleOnLoad=false
                        endVideoBehavior=reset
                        wmode=transparent
                    `}
                />
            </Wrapper>
        </>
    )
}

const VideoCover = styled(Image)`
    object-fit: cover;
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;

    /* Needed to with the specificity war */
    &.gatsby-image-wrapper {
        position: absolute;
    }
`

const Wrapper = styled.div<{$aspectRatio: number; $maxHeight: number}>`
    position: relative;
    padding-bottom: ${(props) => `min(${props.$aspectRatio}%, ${props.$maxHeight}px) `};
    margin-right: auto;
    margin-left: auto;
    isolation: isolate;
`

const StyledPlayButton = styled(PlayButton)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
`

interface WistiaWrapperProps {
    $idleVideo: boolean
    $videoEnded: boolean
}

const WistiaWrapper = styled.div<WistiaWrapperProps>`
    position: absolute;
    display: ${(props) => (props.$idleVideo ? 'none' : 'initial')};
    margin: 0 auto;
    pointer-events: ${(props) => (props.$videoEnded ? 'none' : 'unset')};
    opacity: ${(props) => (props.$videoEnded ? 0 : 1)};
    inset: 0;
`

export default InlineWistiaVideo
